/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  isDraftTeam,
  startTeamImageUrl,
  teamRosterLabel,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import type { StartTeam } from '@sportninja/common/features/registration/startStep/types'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import Text from 'src/components/ts-components/Text'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface Props {
  team: StartTeam
}

/**
 * The team the reader already chose, rendered as a column inside the selected
 * division card.
 *
 * The register step used to ask for the team again, in a dropdown fed by its
 * own request. The start step now settles it, so this states the answer rather
 * than re-asking. It carries no action of its own: the card's single "Go Back
 * To Change" returns to the step where both the division and the team were
 * chosen, so a second button would go to exactly the same place.
 */
const SelectedTeamCard = ({ team }: Props) => {
  const isDraft = isDraftTeam(team)
  const roster = teamRosterLabel(team)
  const logo = resolveAssetUrl(startTeamImageUrl(team))

  return (
    <div css={infoRow}>
      <img
        css={logoStyle}
        src={isDraft ? BUCKET_IMAGE_SRC : logo || TEAM_PLACEHOLDER}
        alt=''
      />
      <div css={detailsColumn}>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.MAGNESIUM}
        >
          Selected Team
        </Text>
        <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
          {team.name ?? 'Unnamed Team'}
        </Text>
        {/* Only what the payload actually carries: a roster count is absent on
            some team shapes, and "0 Players" would be a claim the API never
            made. */}
        {roster ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.MAGNESIUM}
          >
            {roster}
          </Text>
        ) : null}
      </div>
    </div>
  )
}

export default SelectedTeamCard

/* Top-aligned so the logo sits level with the "Selected Team" label rather than
   drifting down to the middle of the two or three lines beside it. */
const infoRow = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  min-width: 0;
`

const logoStyle = css`
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: 8px;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_800};
`

const detailsColumn = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`
